import { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { useChartContext } from '../ChartProvider'

function cleanChartConfig (config) {
  Object.keys(config).forEach((key) => {
    if (typeof config[key] === 'undefined') {
      delete config[key]
    }
  })

  return config
}

function ComponentTreeMap ({
  seriesId,
  data,
  name,
  zlevel,
  z,
  left,
  top,
  right,
  bottom,
  width,
  height,
  squareRatio,
  leafDepth,
  drillDownIcon,
  roam,
  scaleLimit,
  nodeClick,
  zoomToNodeRatio,
  visualDimension,
  visualMin,
  visualMax,
  colorAlpha,
  colorSaturation,
  colorMappingBy,
  visibleMin,
  childVisibleMin,
  label,
  upperLabel,
  itemStyle,
  emphasis,
  blur,
  select,
  selectedMode,
  breadcrumb,
  labelLine,
  labelLayout,
  levels,
  silent,
  animationDuration,
  animationEasing,
  animationDelay,
  tooltip,
  cursor
}) {
  const { setSeries } = useChartContext()

  useLayoutEffect(() => {
    console.log('treemap layout effect')
    setSeries(seriesId, cleanChartConfig({
      type: 'treemap',
      id: seriesId,
      data,
      name,
      zlevel,
      z,
      left,
      top,
      right,
      bottom,
      width,
      height,
      squareRatio,
      leafDepth,
      drillDownIcon,
      roam,
      scaleLimit,
      nodeClick,
      zoomToNodeRatio,
      visualDimension,
      visualMin,
      visualMax,
      colorAlpha,
      colorSaturation,
      colorMappingBy,
      visibleMin,
      childVisibleMin,
      label,
      upperLabel,
      itemStyle,
      emphasis,
      blur,
      select,
      selectedMode,
      breadcrumb,
      labelLine,
      labelLayout,
      levels,
      silent,
      animationDuration,
      animationEasing,
      animationDelay,
      tooltip,
      cursor
    }))
  }, [
    seriesId,
    data,
    name,
    zlevel,
    z,
    left,
    top,
    right,
    bottom,
    width,
    height,
    squareRatio,
    leafDepth,
    drillDownIcon,
    roam,
    scaleLimit,
    nodeClick,
    zoomToNodeRatio,
    visualDimension,
    visualMin,
    visualMax,
    colorAlpha,
    colorSaturation,
    colorMappingBy,
    visibleMin,
    childVisibleMin,
    label,
    upperLabel,
    itemStyle,
    emphasis,
    blur,
    select,
    selectedMode,
    breadcrumb,
    labelLine,
    labelLayout,
    levels,
    silent,
    animationDuration,
    animationEasing,
    animationDelay,
    tooltip,
    cursor,
    setSeries
  ])

  return null
}

ComponentTreeMap.propTypes = {
  seriesId: PropTypes.string,
  name: PropTypes.string,
  data: PropTypes.array,

  /** zlevel value of all graphical elements in Line.
   zlevel is used to make layers with Canvas. Graphical elements with different zlevel values will be placed in
   different Canvases, which is a common optimization technique. We can put those frequently changed elements
   (like those with animations) to a separate zlevel. Notice that too many Canvases will increase memory cost,
   and should be used carefully on mobile phones to avoid crash.
   Canvases with bigger zlevel will be placed on Canvases with smaller zlevel. */
  zlevel: PropTypes.number,

  /** z value of all graphical elements in Line, which controls order of drawing graphical components. Components with smaller z values may be overwritten by those with larger z values.
   z has a lower priority to zlevel, and will not create new Canvas. */
  z: PropTypes.number,

  /** Distance between treemap component and the left side of the container.
   left can be a pixel value like 20; it can also be a percentage value relative to container width like '20%'; and it can also be 'left', 'center', or 'right'.
   If the left value is set to be 'left', 'center', or 'right', then the component will be aligned automatically based on position. */
  left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Distance between treemap component and the top side of the container.
   top can be a pixel value like 20; it can also be a percentage value relative to container width like '20%'; and it can also be 'top', 'middle', or 'bottom'.
   If the top value is set to be 'top', 'middle', or 'bottom', then the component will be aligned automatically based on position. */
  top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Distance between treemap component and the right side of the container.
   right can be a pixel value like 20; it can also be a percentage value relative to container width like '20%'.
   Adaptive by default. */
  right: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Distance between treemap component and the bottom side of the container.
   bottom can be a pixel value like 20; it can also be a percentage value relative to container width like '20%'.
   Adaptive by default. */
  bottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Width of treemap component */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Height of treemap component. */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** The expected square ratio. Layout would approach the ratio as close as possible.
   It defaults to be the golden ratio: 0.5 * (1 + Math.sqrt(5)). */
  squareRatio: PropTypes.number,

  /**
   When leafDepth is set, the feature "drill down" is enabled, which means when clicking a tree node, this node will be set as root and its children will be shown.
   leafDepth represents how many levels are shown at most. For example, when leafDepth is set to 1, only one level will be shown.
   leafDepth is null/undefined by default, which means that "drill down" is disabled. */
  leafDepth: PropTypes.number,

  /** Marker when the node is able to be drilled down. */
  drillDownIcon: PropTypes.string,

  /** Whether to enable dragging roam (move and zoom). Optional values are:
   false: roam is disabled.
   'scale' or 'zoom': zoom only.
   'move' or 'pan': move (translation) only.
   true: both zoom and move (translation) are available. */
  roam: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /** Limit of scaling, with min and max. */
  scaleLimit: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number
  }),

  /** The behaviour when clicking a node. Optional values are:
   false: Do nothing after clicked.
   'zoomToNode': Zoom to clicked node.
   'link': If there is link in node data, do hyperlink jump after clicked. */
  nodeClick: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),

  /** The treemap will be auto zoomed to a appropriate ratio when a node is clicked (when nodeClick is set as 'zoomToNode' and no drill down happens). This configuration item indicates the ratio. */
  zoomToNodeRatio: PropTypes.number,

  /** treemap is able to map any dimensions of data to visual.
   The value of series-treemap.data can be an array. And each item of the array represents a "dimension". visualDimension specifies the dimension on which visual mapping will be performed.
   About visual encoding, see details in series-treemap.levels. */
  visualDimension: PropTypes.number,

  /**
   The minimal value of current level. Auto-statistics by default.
   When colorMappingBy is set to 'value', you are able to specify extent manually for visual mapping by specifying visualMin or visualMax. */
  visualMin: PropTypes.number,

  /** The maximal value of current level. Auto-statistics by default.
   When colorMappingBy is set to 'value', you are able to specify extent manually for visual mapping by specifying visualMin or visualMax. */
  visualMax: PropTypes.number,

  /** It indicates the range of transparent rate (color alpha) for nodes of the series
   . The range of values is 0 ~ 1.
   For example, colorAlpha can be [0.3, 1].
   About visual encoding, see details in series-treemap.levels. */
  colorAlpha: PropTypes.array,

  /** It indicates the range of saturation (color alpha) for nodes of the series.
   The range of values is 0 ~ 1.
   For example, colorSaturation can be [0.3, 1].
   About visual encoding, see details in series-treemap.levels. */
  colorSaturation: PropTypes.array,

  /** Specify the rule according to which each node obtain color from color list. Optional values:
   'value': Map series-treemap.data.value to color.
   In this way, the color of each node indicate its value.
   visualDimension can be used to specify which dimension of data is used to perform visual mapping.
   'index': Map the index (ordinal number) of nodes to color. Namely, in a level, the first node is mapped to the first color of color list, and the second node gets the second color.
   In this way, adjacent nodes are distinguished by color.
   'id': Map series-treemap.data.id to color.
   Since id is used to identify node, if user call setOption to modify the tree, each node will remain the original color before and after setOption called. See this example.
   About visual encoding, see details in series-treemap.levels. */
  colorMappingBy: PropTypes.oneOf(['index', 'value', 'id']),

  /** A node will not be shown when its area size is smaller than this value (unit: px square).
   In this way, tiny nodes will be hidden, otherwise they will huddle together. When user zoom the treemap, the area size will increase and the rectangle will be shown if the area size is larger than this threshold.
   About visual encoding, see details in series-treemap.levels. */
  visibleMin: PropTypes.number,

  /** Children will not be shown when area size of a node is smaller than this value (unit: px square).
   This can hide the details of nodes when the rectangular area is not large enough. When users zoom nodes, the child node would show if the area is larger than this threshold.
   About visual encoding, see details in series-treemap.levels. */
  childVisibleMin: PropTypes.number,

  /** label describes the style of the label in each node. */
  label: PropTypes.object,

  /** upperLabel is used to specify whether show label when the node has children. When upperLabel.show is set as true, the feature that "show parent label" is enabled.
   The same as series-treemap.label, the option upperLabel can be placed at the root of series-treemap directly, or in series-treemap.level, or in each item of series-treemap.data.
   Specifically, series-treemap.label specifies the style when a node is a leaf, while upperLabel specifies the style when a node has children, in which case the label is displayed in the inner top of the node. */
  upperLabel: PropTypes.object,

  /** Style the chart items */
  itemStyle: PropTypes.object,

  /** Highlight style of the graphic. */
  emphasis: PropTypes.object,

  /** Configurations of blur state. */
  blur: PropTypes.object,

  /** Configurations of select state. Available when selectedMode is set. */
  select: PropTypes.object,

  /** Selected mode. It is disabled by default, and you may set it to be true to enable it.
   Besides, it can be set to 'single', 'multiple' or 'series', for single selection, multiple selections and whole series selection. */
  selectedMode: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['single', 'multiple', 'series'])]),

  /** To show the path of the current node. */
  breadcrumb: PropTypes.object,

  /** Configuration of label guide line. */
  labelLine: PropTypes.object,

  /** Unified layout configuration of labels.
   It provide a chance to adjust the labels' (x, y) position, alignment based on the original layout each series provides.
   This option can be a callback with following parameters. */
  labelLayout: PropTypes.object,

  /** Multiple Levels Configuration
   treemap adopts 4-level configuration:
   "each node" --> "each level" --> "each series".
   That is, we can configurate each node, can also configurate each level of the tree, or set overall configurations on each series. The highest priority is node configuration. */
  levels: PropTypes.array,

  /** Whether to ignore mouse events. Default value is false, for triggering and responding to mouse events. */
  silent: PropTypes.bool,

  /** Duration of the first animation, which supports callback function for different data to have different animation effect: */
  animationDuration: PropTypes.number,

  /** Easing method used for the first animation. Varied easing effects can be found at easing effect example. */
  animationEasing: PropTypes.string,

  /** Delay before updating the first animation, which supports callback function for different data to have different animation effect. */
  animationDelay: PropTypes.number,

  tooltip: PropTypes.object,

  /** The mouse style when mouse hovers on an element, the same as cursor property in CSS. */
  cursor: PropTypes.string
}

ComponentTreeMap.defaultProps = {
}

export default ComponentTreeMap
